import { DeleteOutlined, EditOutlined, FileExcelOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { Button, Col, Popconfirm, Row, Space, Table, Tooltip } from 'antd';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { dateCommon } from '../../helpers/common';
import { Links } from '../../helpers/Links';
import { BienBanKiemKe } from '../../models/taisan/BienBanKiemKe';
import { DotKiemKeTaiSan } from '../../models/taisan/DotKiemKeTaiSan';
import { layDsBienBanKiemKe, xoaBienBanKiemKe, xuatExcelBienBanKiemKePhongBan } from '../../services/bienBanKiemKe';

interface Props {
    dotKiemKe: DotKiemKeTaiSan | null;
    idDotKiemKeTruoc?: number;
}

const BienBanKiemKeTab = ({ dotKiemKe }: Props) => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [dsBienBan, setDsBienBan] = useState<BienBanKiemKe[]>([]);

    const idDotKiemKe = dotKiemKe?.id;

    const loadDsBienBan = async () => {
        if (!idDotKiemKe) return;
        try {
            setLoading(true);
            const data = await layDsBienBanKiemKe(idDotKiemKe);
            setDsBienBan(data);
        } catch (err: any) {
            toast.error('Lỗi khi tải danh sách biên bản: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (idDotKiemKe) loadDsBienBan();
    }, [idDotKiemKe]);

    const handleTaoMoi = () => {
        navigate(`${Links.TaiSan.ChiTietBienBanKiemKe}?idDotKiemKe=${idDotKiemKe}`);
    };

    const handleChinhSua = (id: number) => {
        navigate(`${Links.TaiSan.ChiTietBienBanKiemKe}?id=${id}&idDotKiemKe=${idDotKiemKe}`);
    };

    const handleXuatExcel = async (record: BienBanKiemKe) => {
        const dotId = record.idDotKiemKe || idDotKiemKe;
        if (!dotId && !record.id) {
            toast.warning('Không tìm thấy thông tin biên bản / đợt kiểm kê');
            return;
        }
        try {
            await xuatExcelBienBanKiemKePhongBan(dotId, record.idDonVi, record.id);
        } catch (err: any) {
            toast.error('Lỗi khi xuất file: ' + err.message);
        }
    };

    const handleXoa = async (id: number) => {
        try {
            await xoaBienBanKiemKe(id);
            toast.success('Đã xóa biên bản');
            loadDsBienBan();
        } catch (err: any) {
            toast.error('Lỗi khi xóa: ' + err.message);
        }
    };

    const columns = [
        {
            title: 'STT',
            key: 'stt',
            width: 50,
            align: 'center' as const,
            render: (_: any, __: any, idx: number) => idx + 1,
        },
        { title: 'Tên biên bản', dataIndex: 'tenBienBan', key: 'tenBienBan' },
        { title: 'Đơn vị', dataIndex: 'tenDonVi', key: 'tenDonVi', width: 200 },
        {
            title: 'Ngày tạo',
            dataIndex: 'ngayKhoiTao',
            key: 'ngayKhoiTao',
            width: 120,
            render: (v: Date) => v ? dateCommon.formatDate(v) : '',
        },
        { title: 'Người tạo', dataIndex: 'nguoiTao', key: 'nguoiTao', width: 150 },
        {
            title: 'Thao tác',
            key: 'action',
            width: 170,
            align: 'center' as const,
            render: (_: any, record: BienBanKiemKe) => (
                <Space>
                    <Tooltip title="Xuất file kiểm kê (Biểu mẫu b - Excel)">
                        <Button
                            size="small"
                            icon={<FileExcelOutlined />}
                            style={{ color: '#28a745', borderColor: '#28a745' }}
                            onClick={() => handleXuatExcel(record)}
                        >
                            Xuất file
                        </Button>
                    </Tooltip>
                    <Tooltip title="Chỉnh sửa">
                        <Button size="small" type="primary" icon={<EditOutlined />} onClick={() => handleChinhSua(record.id!)} />
                    </Tooltip>
                    <Popconfirm title="Xác nhận xóa biên bản này?" onConfirm={() => handleXoa(record.id!)} okText="Xóa" cancelText="Hủy">
                        <Button size="small" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <div>
            <Row justify="space-between" align="middle" style={{ marginBottom: 12 }}>
                <Col>
                    <strong>Danh sách biên bản kiểm kê</strong>
                </Col>
                <Col>
                    <Space>
                        <Button icon={<ReloadOutlined />} onClick={loadDsBienBan} size="small">Làm mới</Button>
                        <Button type="primary" icon={<PlusOutlined />} onClick={handleTaoMoi} size="small">
                            Tạo biên bản
                        </Button>
                    </Space>
                </Col>
            </Row>
            <Table
                columns={columns}
                dataSource={dsBienBan}
                loading={loading}
                rowKey="id"
                size="small"
                pagination={false}
                scroll={{ x: 700 }}
            />
        </div>
    );
};

export default BienBanKiemKeTab;
